import { AllowedCountries } from 'app/lang/constants';

import {
  MIN_LEN_PHONE_NUMBER,
  NOT_NUMBER_CHAR,
  PHONE_DATA,
  TEL_LEN_INPUT_REQ,
} from './constants';

// ---

export function removeNotNumberCharacter(value: string): string {
  return value.replace(NOT_NUMBER_CHAR, '');
}

// ---

export function getAllowedCountry(phone: string): AllowedCountries | string {
  let country = 'default';

  for (const [key, value] of Object.entries(PHONE_DATA)) {
    if (value.dialCode === null) {
      break;
    }

    if (phone.startsWith(value.dialCode)) {
      country = key;
      break;
    }
  }

  return country;
}

// --- exceptions for 'ru' region, if value contains '8' as local format with or instead international format ('7')

export function replaceRuRegionExceptions(phone: string): string {
  const changedValue = phone.slice(0, 2) === PHONE_DATA.RU.exceptions.local
    ? phone.slice(0, 1)
    : phone.slice(0, 2);

  return phone.replace(changedValue, PHONE_DATA.RU.dialCode);
}

// ---

export function getPhoneMask(phone: string): string {
  const country = getAllowedCountry(phone);

  return country !== ''
    ? PHONE_DATA[country as AllowedCountries].mask
    : PHONE_DATA.default.mask;
}

// ---

function getFormattedObject(mask: string, value: string) {
  return (mask.split('').reduce(
    (acc, character) => {
      if (acc.remainingText.length === 0) {
        return acc;
      }

      if (character !== '.') {
        return {
          formattedText: acc.formattedText + character,
          remainingText: acc.remainingText,
        };
      }

      return {
        formattedText: acc.formattedText + acc.remainingText.shift(),
        remainingText: acc.remainingText,
      };
    },
    { formattedText: '', remainingText: value.split('') },
  ));
}

// ---

export function formattingPreparation(value: string): string {
  let onlyNumbers = removeNotNumberCharacter(value);

  if (onlyNumbers.length > TEL_LEN_INPUT_REQ) {            // requirement of TEL-LEN-INPUT is max length 25
    onlyNumbers = onlyNumbers.slice(0, TEL_LEN_INPUT_REQ); // https://confluence.tennisi.it/pages/viewpage.action?pageId=773673526
  }

  if (onlyNumbers.startsWith(PHONE_DATA.RU.exceptions.local)
    || onlyNumbers.startsWith(PHONE_DATA.RU.exceptions.intlWithLocal)) {
    return replaceRuRegionExceptions(onlyNumbers);
  }

  return onlyNumbers;
}

// ---

export function formatPhoneNumber(phone: string): string {
  if (!phone) {
    return phone;
  }

  const formattedPhone = formattingPreparation(phone);

  const mask = getPhoneMask(formattedPhone);

  if (mask === PHONE_DATA.default.mask) {
    return formattedPhone;
  }

  const { formattedText, remainingText } = getFormattedObject(mask, formattedPhone);

  if (remainingText.length > 0) {
    return `${formattedText} ${remainingText.join('')}`;
  }

  return formattedText;
}

// ---

export function isValidPhoneNumber(number: string): boolean {
  const formatted = formattingPreparation(number);
  const mask = getPhoneMask(formatted);

  if (mask === PHONE_DATA.default.mask && formatted.length >= MIN_LEN_PHONE_NUMBER) {
    return true;
  }

  const { formattedText, remainingText } = getFormattedObject(mask, formatted);

  return formattedText.length === mask.length && remainingText.length >= 0;
}

// ---

export function getDialCode(phoneNumber: string) {
  let key: keyof typeof PHONE_DATA;

  for (key in PHONE_DATA) {
    const dialCode = PHONE_DATA[key].dialCode;

    if (dialCode && phoneNumber.startsWith(dialCode)) {
      return dialCode;
    }
  }

  return phoneNumber;
}
